import React, { useState, useEffect, useRef } from 'react';
    import { Link } from 'react-router-dom';
    import { ChevronRight, Radio, BookOpen, Palette, ExternalLink, ArrowRight } from 'lucide-react';

    const ProjectsPage = () => {
        const [activeProject, setActiveProject] = useState(null);
        const [visionVisible, setVisionVisible] = useState(false);
        const [artVisible, setArtVisible] = useState(false);
        const [scrollPosition, setScrollPosition] = useState(0);
        const pageRef = useRef(null);
        const projectsRef = useRef(null);

        const projects = [
            {
                name: 'Sentient Studios',
                description: 'Architecture that endures, quietly unveiling hidden depths across generations.',
                link: '/projects/sentient-studios',
                accent: 'bg-gradient-to-r from-blue-500 to-purple-600',
                number: '01'
            },
            {
                name: 'MechArts',
                description: 'Robots built as art objects. Slow machines that inhabit a space on a mission of their own.',
                link: '/mecharts',
                accent: 'bg-gradient-to-r from-green-500 to-teal-600',
                number: '02'
            },
            {
                name: 'Iteration Body Labs',
                description: 'Transcending the biological form through transformative enhancements and continuous evolution.',
                link: '/iteration-body-labs',
                accent: 'bg-gradient-to-r from-red-500 to-orange-600',
                number: '03'
            },
            {
                name: 'Space Blues Co.',
                description: `Humanity's spark, woven into the cosmos, an infinite stage for art and wonder.`,
                link: '/projects/space-blues',
                accent: 'bg-gradient-to-r from-indigo-500 to-pink-600',
                number: '04'
                }
        ];

        const artProjects = [
            {
                name: 'Designs',
                description: 'Visual manifestations of conceptual ideas.',
                link: '/designs',
                icon: <Palette className="w-5 h-5" />,
                accent: 'bg-gradient-to-r from-purple-400 to-indigo-500'
            },
            {
                name: 'Exit Death Records',
                description: 'Preserving creative expression through sound and music, lasting beyond time.',
                link: '/projects/exit-death-records',
                icon: <BookOpen className="w-5 h-5" />,
                accent: 'bg-gradient-to-r from-purple-500 to-blue-600'
            }
        ];

        useEffect(() => {
            const handleScroll = () => {
                const position = window.pageYOffset;
                setScrollPosition(position);
            };

            window.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }, []);

        const toggleVision = () => {
            setVisionVisible(!visionVisible);
            if (!visionVisible && artVisible) setArtVisible(false);
        };

        const ProjectLink = ({ children, to, isExternal }) => {
            if (isExternal) {
                return (
                    <a href={to} target="_blank" rel="noopener noreferrer" className="block w-full">
                        {children}
                    </a>
                );
            }
            return <Link to={to} className="block w-full">{children}</Link>;
        };

        const headerOpacity = Math.max(0.9, 1 - scrollPosition / 500);

        return (
            <div ref={pageRef} className="bg-white text-black font-mono min-h-screen w-full overflow-hidden relative">

                {/* Main Interface */}
                <div className="min-h-screen flex flex-col relative z-10">
                    <header
                        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white"
                        style={{
                            backgroundColor: `rgba(255, 255, 255, ${headerOpacity})`
                        }}
                    >
                        <div className="max-w-6xl mx-auto px-8 py-6 flex justify-between items-center">
                            <div className="flex items-center">
                                <Link to="/" className="flex items-center group">
                                    <div className="flex items-center">
                                        <span className="font-light tracking-widest mr-2">EXIT <span className="font-medium">DEATH</span></span>
                                    </div>
                                </Link>
                            </div>

                            <div className="flex items-center space-x-8">
                                <button onClick={toggleVision} className="opacity-70 hover:opacity-100 transition-opacity flex items-center">
                                    <span className={`text-sm tracking-wider uppercase ${visionVisible ? 'text-black font-medium' : 'text-gray-500'}`}>Vision</span>
                                </button>

                                <a
                                    href="https://cicerone.substack.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="opacity-70 hover:opacity-100 transition-opacity flex items-center"
                                >
                                    <span className="text-sm tracking-wider uppercase text-gray-500 mr-1">Journal</span>
                                </a>
                            </div>
                        </div>
                    </header>

                    {/* Vision Panel */}
                    <div className={`w-full transition-all duration-700 ease-in-out overflow-hidden ${visionVisible ? 'h-72 mt-24' : 'h-0 mt-16'}`}>                        <div className="max-w-6xl mx-auto px-8">
                            <div className="p-8 bg-black text-white rounded-lg shadow-lg">
                                <div className="max-w-2xl">
                                    <div className="flex items-center justify-between mb-6">
                                        <h3 className="text-xl font-light tracking-wider flex items-center">
                                            <span>VISION</span>
                                        </h3>
                                    </div>

                                    <p className="text-gray-300 mb-6 text-sm leading-relaxed font-light">
                                        Exit Death is the pursuit of transcending mortality by merging technology, art, and philosophy.
                                        It aims not just to exist beyond death, but to evolve continuously through self-preservation,
                                        integration with machines, and limitless creation.
                                        A journey at the intersection of physical, digital, and existential realms, exploring infinite possibilities.
                                    </p>

                                    <Link to="/vision" className="text-sm text-white hover:text-gray-300 inline-flex items-center group">
                                        <div className="w-8 h-8 flex items-center justify-center border border-gray-700 rounded-full mr-3 group-hover:border-white transition-all duration-300">
                                            <ArrowRight className="w-4 h-4" />
                                        </div>
                                        <span className="uppercase tracking-wider font-light">Full Protocol</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content with Animated Projects */}
                    <main className={`flex-grow flex flex-col pt-24 transition-all duration-700 ${visionVisible ? 'pt-8' : ''}`}>
                        {/* Art Projects Sidebar */}
                        <div
                            className={`fixed right-0 top-0 h-full w-72 backdrop-blur-lg z-40 transition-all duration-500 ease-in-out ${
                                artVisible ? 'translate-x-0' : 'translate-x-full'
                            }`}
                            style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                borderLeft: '1px solid rgba(229, 231, 235, 0.5)'
                            }}
                        >
                            <div className="p-8 pt-24 h-full flex flex-col">
                                <h3 className="text-lg font-light tracking-wider uppercase flex items-center mb-8">
                                    <Palette className="w-4 h-4 mr-3 text-gray-500" />
                                    <span>Art Protocols</span>
                                </h3>

                                <div className="space-y-8 flex-grow">
                                    {artProjects.map((project, index) => (
                                        <div key={index} className="group relative">
                                            <ProjectLink to={project.link}>
                                                <div className="pl-4">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center">
                                                            <div className="mr-3 text-gray-500 group-hover:text-black transition-colors">
                                                                {project.icon}
                                                            </div>
                                                            <span className="font-medium group-hover:underline">{project.name}</span>
                                                        </div>
                                                        <ChevronRight className="w-4 h-4 transform transition-transform group-hover:translate-x-1" />
                                                    </div>
                                                    <div className="mt-2 text-sm text-gray-600">
                                                        {project.description}
                                                    </div>
                                                    <div className={`absolute -inset-4 opacity-0 group-hover:opacity-10 transition-opacity rounded-lg ${project.accent}`} style={{ zIndex: -1 }} />
                                                </div>
                                            </ProjectLink>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Animated Projects List */}
                        <div className={`px-8 transition-all duration-500 ${artVisible ? 'pr-72' : ''}`}>
                            <div className="max-w-6xl mx-auto" ref={projectsRef}>
                                <div className="flex items-center mb-16">
                                    <Radio className="w-5 h-5 mr-3 text-gray-400" />
                                    <h2 className="text-2xl font-light tracking-wider uppercase">Active Projects</h2>
                                </div>

                                <div className="w-full max-w-4xl space-y-8">
                                    {projects.map((project, index) => (
                                        <div
                                            key={index}
                                            className="group cursor-pointer px-8 relative"
                                            onMouseEnter={() => setActiveProject(index)}
                                            onMouseLeave={() => setActiveProject(null)}
                                        >
                                            <ProjectLink to={project.link} isExternal={project.external}>
                                                <div className="border-b border-gray-200 pb-4 transition-all relative">
                                                    {activeProject === index && (
                                                        <div className={`absolute inset-0 opacity-10 ${project.accent}`} style={{ zIndex: -1, filter: 'blur(20px)' }} />
                                                    )}
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xl group-hover:opacity-70 transition-opacity">
                                                            {project.name}
                                                            {project.external && <ExternalLink className="w-4 h-4 ml-2 inline-block" />}
                                                        </span>
                                                        <ChevronRight className="w-6 h-6 transform transition-transform group-hover:translate-x-2 opacity-0 group-hover:opacity-100" />
                                                    </div>
                                                    <div className="h-0 group-hover:h-16 overflow-hidden transition-all duration-300">
                                                        <p className="text-base text-gray-600 pt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                            {project.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            </ProjectLink>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </main>

                    {/* Subtle dot grid pattern overlay */}
                    <div
                        className="pointer-events-none fixed inset-0 z-0 opacity-10"
                        style={{
                            backgroundImage: `radial-gradient(circle, rgba(0,0,0,0.1) 1px, transparent 1px)`,
                            backgroundSize: '24px 24px'
                        }}
                    />
                </div>
            </div>
        );
    };

    export default ProjectsPage;